import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { EntityLookup, type EntityLookupValue } from "@/components/shared/EntityLookup";
import { DataTable, type RowAction } from "@/components/data-table/DataTable";
import { usePagedQuery } from "@/components/data-table/usePagedQuery";
import { formatDateBr, hojeStr, toLocalDateTimeString } from "@/lib/format";
import {
  deleteInteracao,
  getInteracao,
  saveInteracao,
  INTERACAO_PATH,
  type InstituicaoInteracao,
  type InstituicaoInteracaoFiltro,
  type Tela,
  type TipoInteracao,
} from "./api";
import { interacaoSchema, type InteracaoFormValues } from "./schema";
import { TIPO_INTERACAO_LABEL } from "./labels";
import { TelaLookup } from "./TelaLookup";

// Porte de app/controllers/consulta-instituicao-interacao.controller.js +
// app/templates/instituicao-interacao/consulta-instituicao-interacao.html.
// Última rota "presa" desde a Fase 2 — dependia do <EntityLookup> (Fase 4) e
// de um segundo lookup (Telas, sp-lookup) com contrato diferente, resolvido
// aqui por um componente dedicado (TelaLookup) em vez de generalizar o
// EntityLookup pra um único uso.
const QUERY_KEY = ["instituicao-interacao"];

const TIPO_LABEL = TIPO_INTERACAO_LABEL;

const columns: ColumnDef<InstituicaoInteracao, unknown>[] = [
  { accessorKey: "id", header: "ID", enableSorting: true },
  {
    accessorKey: "dataInteracao",
    header: "Data Interação",
    enableSorting: true,
    cell: ({ getValue }) => formatDateBr(getValue() as string | null),
  },
  {
    id: "instituicao.nomeFantasia",
    accessorFn: (row) => row.instituicao?.nomeFantasia,
    header: "Instituição",
    enableSorting: true,
  },
  {
    id: "usuarioResponsavel.nome",
    accessorFn: (row) => row.usuarioResponsavel?.nome,
    header: "Usuário",
    enableSorting: true,
  },
  {
    accessorKey: "tipoInteracao",
    header: "Tipo interação",
    enableSorting: true,
    cell: ({ getValue }) => TIPO_LABEL[getValue() as TipoInteracao] ?? String(getValue() ?? ""),
  },
  { accessorKey: "usuarioContato", header: "Usuário contato", enableSorting: true },
  { accessorKey: "descricao", header: "Descrição" },
  {
    id: "tela.tela",
    accessorFn: (row) => row.tela?.tela,
    header: "Tela",
    enableSorting: true,
  },
];

type Modo = "criar" | "editar" | "visualizar";

export default function ConsultaInstituicaoInteracao() {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [modo, setModo] = useState<Modo>("criar");
  const [editingId, setEditingId] = useState<number | undefined>();
  // Registro como veio do GET; o zod descarta chaves fora do schema, então o
  // salvamento sobrepõe os valores do form a ele (ver D14).
  const [registroOriginal, setRegistroOriginal] = useState<InstituicaoInteracao | null>(null);
  const [instituicao, setInstituicao] = useState<EntityLookupValue | null>(null);
  const [tela, setTela] = useState<Tela | null>(null);
  const [instituicaoError, setInstituicaoError] = useState<string | undefined>();

  const query = usePagedQuery<InstituicaoInteracao, InstituicaoInteracaoFiltro>({
    key: QUERY_KEY,
    path: INTERACAO_PATH,
    initialFiltro: {},
  });

  const form = useForm<InteracaoFormValues>({
    resolver: zodResolver(interacaoSchema),
    defaultValues: { dataInteracao: hojeStr(), tipoInteracao: "SU", usuarioContato: "", descricao: "" },
  });

  const excluir = useMutation({
    mutationFn: deleteInteracao,
    onSuccess: () => {
      toast.success("Interação excluída.");
      qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const salvar = useMutation({
    mutationFn: (values: InteracaoFormValues) => {
      // `instituicao`/`tela` (entities aninhadas) ficam de fora: quem manda são
      // instituicaoId/telaId, vindos dos lookups.
      const original: Partial<InstituicaoInteracao> = { ...registroOriginal };
      delete original.instituicao;
      delete original.tela;
      const registro: Partial<InstituicaoInteracao> = {
        ...original,
        ...values,
        // O backend espera LocalDateTime ("yyyy-MM-ddTHH:mm:ss"); o
        // <input type="date"> só entrega "yyyy-MM-dd" (ver E.2/E.6).
        dataInteracao: toLocalDateTimeString(values.dataInteracao),
        id: editingId,
        instituicaoId: instituicao?.id,
        telaId: tela?.telaId,
      };
      return saveInteracao(registro);
    },
    onSuccess: () => {
      toast.success("Interação salva.");
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      setDialogOpen(false);
    },
  });

  function openCreate() {
    setModo("criar");
    setEditingId(undefined);
    setRegistroOriginal(null);
    setInstituicao(null);
    setInstituicaoError(undefined);
    setTela(null);
    form.reset({ dataInteracao: hojeStr(), tipoInteracao: "SU", usuarioContato: "", descricao: "" });
    setDialogOpen(true);
  }

  async function abrirParaVer(row: InstituicaoInteracao, modoAlvo: "editar" | "visualizar") {
    const registro = await getInteracao(row.id);
    setModo(modoAlvo);
    setEditingId(registro.id);
    setRegistroOriginal(registro);
    setInstituicao(registro.instituicao ?? null);
    setInstituicaoError(undefined);
    setTela(registro.tela ?? null);
    // O backend devolve dataInteracao como datetime completo
    // ("yyyy-MM-ddTHH:mm:ss"); <input type="date"> só aceita exatamente
    // "yyyy-MM-dd" no value — com a string inteira ele fica em branco
    // silenciosamente (sem erro nenhum). O legado nunca sofria isso porque
    // dc-input fazia essa normalização de formato por baixo dos panos.
    form.reset({ ...registro, dataInteracao: registro.dataInteracao?.slice(0, 10) ?? "" });
    setDialogOpen(true);
  }

  // A instituição vive fora do react-hook-form (é um lookup, não um <input>
  // registrável), então não entra na validação do zodResolver.
  // form.handleSubmit(handleSubmit) só invoca handleSubmit depois que o RHF
  // valida os próprios campos — se Descrição estiver vazia, o RHF nunca
  // chama handleSubmit, e o erro da Instituição nunca aparecia. Por isso a
  // checagem roda em paralelo, direto no onSubmit do form, não dentro de
  // handleSubmit.
  function validarInstituicao() {
    if (!instituicao) {
      setInstituicaoError("Selecione a instituição.");
      return false;
    }
    setInstituicaoError(undefined);
    return true;
  }

  function handleSubmit(values: InteracaoFormValues) {
    if (!validarInstituicao()) return;
    salvar.mutate(values);
  }

  function onFormSubmit(e: FormEvent<HTMLFormElement>) {
    validarInstituicao();
    void form.handleSubmit(handleSubmit, () =>
      toast.error("Existem campos inválidos. Verifique os campos destacados em vermelho."),
    )(e);
  }

  const actions: RowAction<InstituicaoInteracao>[] = [
    { label: "Visualizar", icon: Eye, onClick: (row) => abrirParaVer(row, "visualizar") },
    { label: "Editar", icon: Pencil, onClick: (row) => abrirParaVer(row, "editar") },
    {
      label: "Excluir",
      icon: Trash2,
      variant: "destructive",
      onClick: (row) => excluir.mutate(row.id),
      confirm: { title: "Excluir esta interação?", confirmLabel: "Excluir" },
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-foreground">Consulta de Interações</h1>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" /> Adicionar Novo
        </Button>
      </div>

      <DataTable
        query={query}
        columns={columns}
        actions={actions}
        getRowId={(r) => r.id}
        exportFilename="interacoes"
        emptyMessage="Nenhuma interação encontrada."
        toolbar={
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-40">
              <Label htmlFor="f-data-inicial">Data inicial</Label>
              <Input
                id="f-data-inicial"
                type="date"
                value={query.draft.dataInicial?.slice(0, 10) ?? ""}
                onChange={(e) =>
                  query.setDraft((f) => ({
                    ...f,
                    dataInicial: e.target.value ? toLocalDateTimeString(e.target.value) : undefined,
                  }))
                }
              />
            </div>
            <div className="w-40">
              <Label htmlFor="f-data-final">Data final</Label>
              <Input
                id="f-data-final"
                type="date"
                value={query.draft.dataFinal?.slice(0, 10) ?? ""}
                onChange={(e) =>
                  query.setDraft((f) => ({
                    ...f,
                    dataFinal: e.target.value ? toLocalDateTimeString(e.target.value, true) : undefined,
                  }))
                }
              />
            </div>
            <div className="w-56">
              <Label htmlFor="f-descricao">Descrição</Label>
              <Input
                id="f-descricao"
                value={query.draft.descricao ?? ""}
                onChange={(e) => query.setDraft((f) => ({ ...f, descricao: e.target.value }))}
              />
            </div>
            <div className="w-44">
              <Label htmlFor="f-usuario">Usuário responsável</Label>
              <Input
                id="f-usuario"
                value={query.draft.usuario ?? ""}
                onChange={(e) => query.setDraft((f) => ({ ...f, usuario: e.target.value }))}
              />
            </div>
            <div className="w-44">
              <Label htmlFor="f-tipo">Situação</Label>
              <Select
                value={query.draft.tipoInteracao ?? "__all__"}
                onValueChange={(v) => query.setDraft((f) => ({ ...f, tipoInteracao: v === "__all__" ? undefined : v }))}
              >
                <SelectTrigger id="f-tipo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos</SelectItem>
                  {Object.entries(TIPO_LABEL).map(([v, label]) => (
                    <SelectItem key={v} value={v}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-56">
              <EntityLookup
                label="Instituição"
                entityPath="instituicao/Instituicao"
                searchByCodeAndName
                displayCode
                value={query.draft.instituicao ?? null}
                onChange={(v) =>
                  query.setDraft((f) => ({ ...f, instituicao: v ?? undefined, instituicaoId: v?.id }))
                }
              />
            </div>
            <div className="w-48">
              <TelaLookup
                value={query.draft.tela ?? null}
                onChange={(v) => query.setDraft((f) => ({ ...f, tela: v ?? undefined, telaId: v?.telaId }))}
              />
            </div>
            <Button onClick={query.search}>Buscar</Button>
          </div>
        }
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Interação</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={onFormSubmit} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <FormField
                  control={form.control}
                  name="dataInteracao"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Data de interação</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="tipoInteracao"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo interação</FormLabel>
                      <Select value={field.value} onValueChange={(v) => v && field.onChange(v)}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Object.entries(TIPO_LABEL).map(([v, label]) => (
                            <SelectItem key={v} value={v}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="usuarioContato"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Usuário contato</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <EntityLookup
                label="Instituição"
                entityPath="instituicao/Instituicao"
                searchByCodeAndName
                displayCode
                value={instituicao}
                onChange={(v) => {
                  setInstituicao(v);
                  setInstituicaoError(undefined);
                }}
                required
                errorMessage={instituicaoError}
              />

              <TelaLookup value={tela} onChange={setTela} />

              <FormField
                control={form.control}
                name="descricao"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Descrição</FormLabel>
                    <FormControl>
                      <Textarea rows={6} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                {modo !== "visualizar" && (
                  <Button type="submit" disabled={salvar.isPending}>
                    {salvar.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {salvar.isPending ? "Salvando..." : "Salvar"}
                  </Button>
                )}
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
